'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircleIcon } from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  type Country,
  CountryDropdown,
} from '@/components/ui/country-dropdown';
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Skeleton } from '@/components/ui/skeleton';

import { useGetOptInConfig, useSubmitOptIn } from '../api/index';

const schema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().optional(),
  email: z.string().email('Enter a valid email').optional().or(z.literal('')),
  phone: z.string().min(6, 'Enter your mobile number'),
  // Mirrors the server rule: absence is never consent.
  consentSms: z.boolean().refine((v) => v === true, {
    message: 'Please tick the box to receive text messages',
  }),
  // NOT required, and not refined. Marketing consent has to be declinable on
  // its own or it is bundled with the transactional consent, which is what
  // carriers reject (error 30913).
  consentMarketing: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

export function OptInForm({ organizationSlug }: { organizationSlug: string }) {
  // PhoneInput holds the LOCAL number; the calling code is picked separately
  // and joined on submit, so the API always receives E.164.
  const [callingCode, setCallingCode] = useState('+1');
  const { config, isLoading, isError } = useGetOptInConfig(organizationSlug);
  const { submitOptIn, isSubmitting, isSuccess } = useSubmitOptIn({
    organizationSlug,
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      // MUST start false — a pre-ticked box is not valid consent under the
      // TCPA and carrier reviewers reject campaigns for it.
      consentSms: false,
      // Both boxes start unticked. A pre-ticked consent is not consent.
      consentMarketing: false,
    },
  });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-md space-y-4 p-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !config) {
    return (
      <div className="mx-auto max-w-md p-6 text-center">
        <p className="text-muted-foreground">
          This sign-up page isn&apos;t available.
        </p>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="mx-auto max-w-md p-6">
        <Card>
          <CardHeader className="items-center text-center">
            <CheckCircleIcon className="size-10 text-green-600 dark:text-green-400" />
            <CardTitle>You&apos;re signed up</CardTitle>
            <CardDescription>
              You&apos;ll get text updates from {config.businessName}. Reply
              STOP at any time to unsubscribe.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md p-6">
      <Card>
        <CardHeader>
          {config.logo && (
            <img
              alt={config.businessName}
              className="mb-2 h-12 w-auto object-contain"
              src={config.logo}
            />
          )}
          <CardTitle>Get text updates from {config.businessName}</CardTitle>
          <CardDescription>
            Appointment reminders and occasional offers, straight to your phone.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={form.handleSubmit((values) =>
              submitOptIn({
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email || undefined,
                phone: `${callingCode}${values.phone}`,
                consentSms: values.consentSms,
                ...(config.gdprConsentComplianceEnabled === true
                  ? { consentMarketing: values.consentMarketing }
                  : { consentSmsMarketing: values.consentMarketing }),
              })
            )}
          >
            <FieldGroup>
              <Controller
                control={form.control}
                name="firstName"
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={field.name}>First name</FieldLabel>
                    <Input
                      {...field}
                      aria-invalid={fieldState.invalid}
                      autoComplete="given-name"
                      id={field.name}
                    />
                    {fieldState.error && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                )}
              />
              <Controller
                control={form.control}
                name="lastName"
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={field.name}>
                      Last name (optional)
                    </FieldLabel>
                    <Input
                      {...field}
                      aria-invalid={fieldState.invalid}
                      autoComplete="family-name"
                      id={field.name}
                    />
                  </Field>
                )}
              />
              <Controller
                control={form.control}
                name="phone"
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={field.name}>Mobile number</FieldLabel>
                    <div className="flex">
                      <CountryDropdown
                        defaultValue="USA"
                        inline
                        onChange={(country: Country) => {
                          const prefix = country.countryCallingCodes[0];
                          if (prefix) setCallingCode(prefix);
                        }}
                      />
                      <PhoneInput
                        aria-invalid={fieldState.invalid}
                        callingCode={callingCode}
                        id={field.name}
                        onChange={field.onChange}
                        value={field.value}
                      />
                    </div>
                    {fieldState.error && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                )}
              />
              <Controller
                control={form.control}
                name="email"
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={field.name}>
                      Email (optional)
                    </FieldLabel>
                    <Input
                      {...field}
                      aria-invalid={fieldState.invalid}
                      autoComplete="email"
                      id={field.name}
                      type="email"
                    />
                    {fieldState.error && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                )}
              />

              {/* TWO independent checkboxes. A single box covering both
                  consents was rejected by the carriers under error 30913:
                  "marketing consent must be collected separately". Someone must
                  be able to take reminders without taking offers. */}
              <Controller
                control={form.control}
                name="consentSms"
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <div className="flex items-start gap-3">
                      <Checkbox
                        aria-invalid={fieldState.invalid}
                        checked={field.value}
                        id={field.name}
                        onCheckedChange={(checked) =>
                          field.onChange(checked === true)
                        }
                      />
                      <FieldLabel
                        className="text-sm font-normal leading-snug"
                        htmlFor={field.name}
                      >
                        {/* Rendered from the server so the page, the stored
                            evidence and the Twilio campaign never diverge. */}
                        {config.transactionalDisclosureText}
                      </FieldLabel>
                    </div>
                    {fieldState.error && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                )}
              />

              <Controller
                control={form.control}
                name="consentMarketing"
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <div className="flex items-start gap-3">
                      <Checkbox
                        aria-invalid={fieldState.invalid}
                        checked={field.value}
                        id={field.name}
                        onCheckedChange={(checked) =>
                          field.onChange(checked === true)
                        }
                      />
                      <FieldLabel
                        className="text-sm font-normal leading-snug"
                        htmlFor={field.name}
                      >
                        {config.marketingDisclosureText}
                      </FieldLabel>
                    </div>
                    {fieldState.error && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                )}
              />

              <Button disabled={isSubmitting} type="submit">
                {isSubmitting ? 'Signing you up…' : 'Sign me up'}
              </Button>

              {(config.privacyPolicyUrl || config.termsUrl) && (
                <p className="text-center text-xs text-muted-foreground">
                  {config.privacyPolicyUrl && (
                    <a
                      className="underline"
                      href={config.privacyPolicyUrl}
                      rel="noreferrer noopener"
                      target="_blank"
                    >
                      Privacy Policy
                    </a>
                  )}
                  {config.privacyPolicyUrl && config.termsUrl && ' · '}
                  {config.termsUrl && (
                    <a
                      className="underline"
                      href={config.termsUrl}
                      rel="noreferrer noopener"
                      target="_blank"
                    >
                      Terms &amp; Conditions
                    </a>
                  )}
                </p>
              )}
            </FieldGroup>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
